import { Card, Col, Divider, Input, Row, Select, Space, Tag } from 'antd'
import { Content } from 'antd/lib/layout/layout'
import { useEffect, useMemo, useState } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import './issueTagging.less'
import { useAppSelector } from '../../hooks/hooks'
import { save_tagging_btn_label } from '../../wording'
import { selectTagOptions } from '../home/homeSlice'

const IssueTagging = ({ parent_type, tagging_list, setTaggingList, on_save }) => {
  // display main categories
  const [display_main_array, setDisplayMainArray] = useState([])
  // display pop ups (sub-questions)
  const [display_sub_array, setDisplaySubArray] = useState([])

  const [main_array_data, setMainArrayData] = useState([])
  const [sub_array_data, setSubArrayData] = useState([])
  const [input, setInput] = useState([])

  const [needClearing, setNeedClearing] = useState(false)

  // used to force re-mount transparent <Select> so it clears after save
  const [transparentResetKey, setTransparentResetKey] = useState(0)

  const issue_tagging_options = useAppSelector(selectTagOptions)

  const [error, setError] = useState({
    is_error: false,
    error_message: ''
  })

  // --- Helpers to read config shape safely ---
  // tagConfig: main tree structure
  const tagConfig = useMemo(
    () => (issue_tagging_options?.tag ? issue_tagging_options.tag : issue_tagging_options),
    [issue_tagging_options]
  )

  // transparentConfig: [{ key: 'main_category', label: 'หัวข้อ' }, ...]
  const transparentConfig = useMemo(
    () => issue_tagging_options?.transparentConfig || [],
    [issue_tagging_options]
  )

  // set of transparent keys for quick lookup
  const transparentKeySet = useMemo(
    () => new Set(transparentConfig.map(c => c.key)),
    [transparentConfig]
  )

  // --- Utils ---
  // Keep the *last* occurrence for a given (id,key) pair
  const removeDuplicateKeyFromArray = arr => {
    if (!Array.isArray(arr)) return []
    const map = new Map()
    arr.forEach(item => {
      if (!item) return
      const k = `${item.id ?? ''}::${item.key ?? ''}`
      map.set(k, item) // last wins
    })
    return Array.from(map.values())
  }

  const removeSameKeyFromArr = (arr, key) => {
    if (!Array.isArray(arr)) return []
    return arr.filter(obj => obj.key !== key)
  }

  const generateIDForTree = (nodes, hist_id = '0') => {
    if (!Array.isArray(nodes)) return []
    return nodes.map((x, i) => {
      const id = '' + hist_id + '' + i
      const base = {
        ...x,
        id,
        parent_id: hist_id
      }
      if (x.choices && Array.isArray(x.choices)) {
        return {
          ...base,
          choices: generateIDForTree(x.choices, id)
        }
      }
      return base
    })
  }

  const removeSameParentMembers = (arr, current_parent_id) => {
    if (!Array.isArray(arr)) return []

    let newArr = []
    let removedArr = []

    arr.forEach(item => {
      if (item.parent_id !== current_parent_id) {
        newArr.push(item)
      } else {
        removedArr.push(item)
      }
    })

    let lastNewArr = []

    if (removedArr.length !== 0) {
      newArr.forEach(item => {
        let shouldKeep = true
        removedArr.forEach(remove_item => {
          const remove_item_id_length = (remove_item.id || '').length
          const item_id_length = (item.id || '').length
          if (
            remove_item_id_length < item_id_length &&
            item.id &&
            remove_item.id &&
            item.id.substring(0, remove_item_id_length) === remove_item.id
          ) {
            shouldKeep = false
          }
        })
        if (shouldKeep) {
          lastNewArr.push(item)
        }
      })
    } else {
      lastNewArr = newArr
    }

    return lastNewArr
  }

  // --- Display helpers (tree) ---
  const treeHandler = choice => {
    if (!choice) return null

    // If this node is hidden, don't render this level but still render children (if any)
    if (choice.hidden) {
      if (Array.isArray(choice.choices) && choice.choices.length > 0) {
        return renderDisplaySubArray(choice.choices)
      }
      return null
    }

    const nodeKey = choice.id || choice.key

    switch (choice.inputType) {
      case 'multiple': {
        // multiple selection (currently: just recurse children to show deeper controls)
        if (!choice.choices) return null
        return choice.choices.map(child => treeHandler(child))
      }
      case 'mutual': {
        let options = []
        choice?.choices?.forEach(choice_item => {
          if (choice_item.hidden) return
          options.push({
            label: choice_item.description,
            value: JSON.stringify(choice_item)
          })
        })

        const handleChange = value => {
          if (value) {
            const jsonValue = JSON.parse(value)
            setSubArrayData(prevArr => {
              return removeDuplicateKeyFromArray([
                ...removeSameParentMembers(prevArr, jsonValue.parent_id),
                jsonValue
              ])
            })
            setInput(prevArr => {
              return removeDuplicateKeyFromArray([
                ...removeSameParentMembers(prevArr, jsonValue.parent_id),
                jsonValue
              ])
            })
          } else {
            // cleared
            setSubArrayData(prevArr => [...removeSameParentMembers(prevArr, choice.id)])
            setInput(prevArr => [...removeSameParentMembers(prevArr, choice.id)])
          }
        }

        return (
          <div key={`${nodeKey}_${transparentResetKey}`}>
            {choice.description}
            <Select
              key={`${nodeKey}_select_${transparentResetKey}`}
              allowClear
              showSearch
              filterOption={(inputText, option) =>
                (option?.label ?? '').toLowerCase().includes(inputText.toLowerCase())
              }
              defaultValue=""
              style={{ width: '100%' }}
              bordered
              onChange={handleChange}
              options={options}
            />
          </div>
        )
      }
      case 'property': {
        // must answer all children
        return (
          <div key={nodeKey}>
            {renderDisplaySubArray(choice.choices || [])}
          </div>
        )
      }
      case 'text': {
        const handleChange = event => {
          const newChoice = { ...choice, value: event.target.value }
          setInput(prevArr => {
            // last value wins for this (id,key)
            return removeDuplicateKeyFromArray([...prevArr, newChoice])
          })
        }
        return (
          <div key={nodeKey}>
            {choice.description}
            <Input onChange={handleChange} />
          </div>
        )
      }
      default:
        return null
    }
  }

  const renderDisplaySubArray = sub_array => {
    if (!Array.isArray(sub_array)) return null
    return sub_array.map(choice => treeHandler(choice))
  }

  // --- Special: render transparent root (e.g. main_category) as one big select ---
  const renderTransparentRoot = (transparentChoice, rootTagConfig, resetKey) => {
    if (!transparentChoice || !Array.isArray(transparentChoice.choices)) return null

    const transparentRootId = transparentChoice.id
    const options = []

    // Flatten grandchildren: main_category -> mainNode -> subNode
    transparentChoice.choices.forEach(mainNode => {
      if (!Array.isArray(mainNode.choices)) return
      mainNode.choices.forEach(subNode => {
        if (subNode.hidden) return
        options.push({
          label: subNode.description,
          value: JSON.stringify({
            transparentRootId,
            mainNode,
            subNode
          })
        })
      })
    })

    const handleChange = value => {
      if (!value) {
        // clear all answers under this transparent root
        setSubArrayData(prev =>
          prev.filter(
            item =>
              !(item.id && String(item.id).startsWith(transparentChoice.id)) &&
              !(item.parent_id && String(item.parent_id).startsWith(transparentChoice.id))
          )
        )
        setInput(prev =>
          prev.filter(
            item =>
              !(item.id && String(item.id).startsWith(transparentChoice.id)) &&
              !(item.parent_id && String(item.parent_id).startsWith(transparentChoice.id))
          )
        )
        return
      }

      const meta = JSON.parse(value)
      const { transparentRootId: rootId, mainNode, subNode } = meta

      const mainAnswer = { ...mainNode }
      const subAnswer = { ...subNode }

      // 1) For UI
      setSubArrayData(prev => {
        const filtered = prev.filter(
          item =>
            !(item.id && String(item.id).startsWith(rootId)) &&
            !(item.parent_id && String(item.parent_id).startsWith(rootId))
        )
        return removeDuplicateKeyFromArray([...filtered, subAnswer])
      })

      // 2) For checkAnswer
      setInput(prev => {
        const filtered = prev.filter(
          item =>
            !(item.id && String(item.id).startsWith(rootId)) &&
            !(item.parent_id && String(item.parent_id).startsWith(rootId))
        )
        return removeDuplicateKeyFromArray([...filtered, mainAnswer, subAnswer])
      })
    }

    const handleClear = () => handleChange(null)

    // Find matching config entry: { key, label }
    const transparentConfigEntry = transparentConfig.find(
      cfg => cfg.key === transparentChoice.key
    )

    const dynamicLabel =
      transparentConfigEntry?.label || rootTagConfig?.transparentLabel || 'หัวข้อ'

    return (
      <div key={`${transparentChoice.key}_${resetKey}`}>
        {dynamicLabel}
        <Select
          key={`${transparentChoice.key}_select_${resetKey}`}
          allowClear
          onClear={handleClear}
          showSearch
          filterOption={(inputText, option) =>
            (option?.label ?? '').toLowerCase().includes(inputText.toLowerCase())
          }
          defaultValue=""
          style={{ width: '100%' }}
          bordered
          onChange={handleChange}
          options={options}
        />
      </div>
    )
  }

  // --- Display helpers (main) ---
  const renderDisplayMainArray = (main_array, rootTagConfig) => {
    if (!Array.isArray(main_array)) return null
    return main_array.map(choice => {
      const nodeKey = choice.id || choice.key
      // If this key is in transparentConfig, we render the special flattened UI
      if (transparentKeySet.has(choice.key) && choice.inputType === 'mutual') {
        return renderTransparentRoot(choice, rootTagConfig, transparentResetKey)
      }
      return (
        <div key={nodeKey}>
          {treeHandler(choice)}
        </div>
      )
    })
  }

  // --- EFFECT: initial & re-init when tag changes ---
  useEffect(() => {
    if (!tagConfig) return

    const withIds = {
      ...tagConfig,
      choices: generateIDForTree(tagConfig?.choices)
    }

    setMainArrayData(withIds.choices)
    setDisplayMainArray(renderDisplayMainArray(withIds.choices, withIds))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagConfig, transparentKeySet, transparentResetKey])

  // --- EFFECT: re-init after saving / clearing ---
  useEffect(() => {
    if (!needClearing || !tagConfig) return

    const withIds = {
      ...tagConfig,
      choices: generateIDForTree(tagConfig?.choices)
    }

    // reset state
    setSubArrayData([])
    setDisplaySubArray([])
    setInput([])

    setMainArrayData(withIds.choices)
    setDisplayMainArray(renderDisplayMainArray(withIds.choices, withIds))
    setNeedClearing(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needClearing, tagConfig])

  // update sub display when data changes
  useEffect(() => {
    setDisplaySubArray(renderDisplaySubArray(sub_array_data))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub_array_data])

  // --- transform / checkAnswer logic ---
  const transformIntoResult = (dup_options, current) => {
    return current
  }

  const saveResult = () => {
    if (!tagConfig) return
    const withIds = {
      ...tagConfig,
      choices: generateIDForTree(tagConfig?.choices)
    }
    const result = transformIntoResult(withIds, sub_array_data)
    console.log('result ', result)
  }

  const checkAnswer = question => {
    const current_answers = [...input]

    switch (question.inputType) {
      case 'multiple': {
        let answersOfQuestion = []

        question.choices.forEach(choice => {
          let x = checkAnswer(choice)
          if (choice.required && choice.required === true) {
            if (!x) {
              throw new Error('ไม่มีข้อมูล ' + choice.description)
            }
            answersOfQuestion.push(x)
          } else {
            if (x) {
              answersOfQuestion.push(x)
            }
          }
        })

        if (answersOfQuestion.length > 0) {
          return { key: question.key, value: answersOfQuestion, description: question.description }
        } else {
          return { key: question.key, description: question.description }
        }
      }
      case 'mutual': {
        let x = {}
        let isAnswered = false

        question.choices.forEach(choice => {
          current_answers?.forEach(current_answer => {
            if (current_answer.key === choice.key && current_answer.parent_id === question.id) {
              isAnswered = true
              x = checkAnswer(choice)
            }
          })
        })

        if (!isAnswered && question.required) {
          throw new Error('ไม่มีข้อมูล ' + question.description)
        }

        if (x?.key) {
          return { key: question.key, value: x, description: question.description }
        } else {
          return null
        }
      }
      case 'property': {
        let x = []
        question.choices.forEach(choice => {
          let answer = checkAnswer(choice)
          if (!answer) {
            throw new Error('ขาดข้อมูล ' + choice.description)
          } else {
            x.push(checkAnswer(choice))
          }
        })
        x.forEach(xItem => {
          if (!xItem) {
            throw new Error('ข้อมูลไม่ครบ')
          }
        })
        return { key: question.key, value: x, description: question.description }
      }
      case 'text': {
        let value
        let isAnswered = false

        current_answers?.forEach(current_answer => {
          if (current_answer.key === question.key) {
            isAnswered = true
            if (current_answer.value === '') {
              isAnswered = false
            } else {
              value = {
                key: question.key,
                value: current_answer.value,
                description: question.description
              }
            }
          }
        })

        if (!isAnswered && question.required) {
          throw new Error('ไม่มีข้อมูล ' + question.description)
        }

        return value
      }
      default: {
        let value
        let isAnswered = false

        current_answers?.forEach(current_answer => {
          if (current_answer.key === question.key) {
            isAnswered = true
            value = { key: current_answer.key, description: current_answer.description }
          }
        })

        if (!isAnswered && question.required) {
          throw new Error('ไม่มีข้อมูล ' + question.description)
        }

        return value
      }
    }
  }

  // --- Tagging list rendering ---
  const handleRemoveTagList = async list_index => {
    let tagList
    await setTaggingList(prev => {
      tagList = [...prev]
      tagList.splice(list_index, 1)
      return tagList
    })
    on_save(tagList)
  }

  const renderTaggingList = (tagging_item, parent_desc, list_index) => {
    const renderList = (tagging_item, parent_desc) => {
      if (Array.isArray(tagging_item.value)) {
        return (
          <div key={`${list_index}_${tagging_item.key}_array`}>
            {tagging_item.description}
            <div>
              <Tag
                key={`${list_index}_${tagging_item.key}_tag`}
                color="red"
                style={{ padding: '5px' }}
              >
                {tagging_item.value.map((item, index) => {
                  if (!item) return null
                  if (Array.isArray(item.value)) {
                    return item.value.map((value_list, innerIndex) => (
                      <div
                        key={`${list_index}_nested_${value_list?.key || innerIndex}_${item?.key || index}`}
                      >
                        {renderList(value_list, item.description)}
                      </div>
                    ))
                  } else {
                    return (
                      <div key={`${list_index}_leaf_${item?.key || index}`}>
                        {renderList(item.value, item.description)}
                      </div>
                    )
                  }
                })}
              </Tag>
            </div>
          </div>
        )
      } else {
        if (tagging_item.value) {
          return (
            <div style={{ padding: '5px' }}>
              <Tag color="blue" style={{ padding: '5px' }}>
                {tagging_item.description}
                {renderList(tagging_item.value, tagging_item.description)}
              </Tag>
            </div>
          )
        } else {
          if (typeof tagging_item === 'string') {
            return <Tag key={`${list_index}_${parent_desc}_string`} style={{ padding: '5px' }}>{parent_desc}: {tagging_item}</Tag>
          } else {
            return <Tag key={`${list_index}_${tagging_item.key}_desc`} style={{ padding: '5px' }}>{parent_desc}: {tagging_item.description}</Tag>
          }
        }
      }
    }

    return (
      <Card
        key={list_index}
        size="small"
        style={{ width: '100%', overflow: 'auto' }}
        extra={
          <CloseOutlined
            style={{ cursor: 'pointer' }}
            onClick={() => handleRemoveTagList(list_index)}
          />
        }
      >
        <Space wrap style={{ width: '100%' }}>
          {renderList(tagging_item, parent_desc)}
        </Space>
      </Card>
    )
  }

  // --- Save button handler (both layouts) ---
  const handleSave = () => {
    setError({
      is_error: false,
      error_message: ''
    })

    try {
      if (!tagConfig) return

      const withIds = {
        ...tagConfig,
        choices: generateIDForTree(tagConfig?.choices)
      }

      const results = []
      withIds.choices.forEach(option => {
        const ans = checkAnswer(option)
        if (ans) {
          results.push(checkAnswer(option))
        }
      })

      let tag_list
      setTaggingList(prev => {
        tag_list = [...prev, { key: 'initiate', value: results }]
        return tag_list
      })

      // trigger clear for inputs / answers
      setNeedClearing(true)
      // force re-mount transparent Select so it visually resets
      setTransparentResetKey(prev => prev + 1)

      on_save(tag_list)
    } catch (e) {
      setError({
        is_error: true,
        error_message: e.message
      })
    }
  }

  // --- Layouts ---
  if (parent_type === 'auto') {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <div style={{ width: '100%', height: '90%', overflow: 'auto' }}>
          <div>
            {display_main_array}
            <Divider plain />
            {display_sub_array}
          </div>

          <div style={{ width: '100%' }}>
            {tagging_list?.map((tag, index) => {
              return renderTaggingList(tag, null, index)
            })}
          </div>
        </div>
        <Row style={{ height: '10%' }}>
          <Col span={12} style={{ alignSelf: 'center' }}>
            <div>มีข้อมูลเพิ่มเติมทั้งหมด {tagging_list?.length} รายการ</div>
            {error.is_error ? (
              <div style={{ width: '100%', color: 'red' }}>ERROR: {error.error_message}</div>
            ) : null}
          </Col>
          <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={handleSave}
              className="accept-button"
              style={{ marginLeft: 'auto' }}
            >
              {save_tagging_btn_label}
            </button>
          </Col>
        </Row>
      </div>
    )
  }

  // normal (non-auto) layout
  return (
    <Content style={{ height: '100%', width: '100%' }}>
      <Row style={{ width: '100%' }}>
        <Col span={12}>{display_main_array}</Col>
        <Col span={12}>{display_sub_array}</Col>
      </Row>
      {error.is_error ? (
        <div style={{ width: '100%', height: '10%', textAlign: 'right', color: 'red' }}>
          {error.error_message}
        </div>
      ) : null}
      <div style={{ width: '100%', height: '10%', textAlign: 'right' }}>
        <button
          onClick={handleSave}
          className="accept-button"
          style={{ marginLeft: 'auto' }}
        >
          {save_tagging_btn_label}
        </button>
      </div>
      <Row style={{ width: '100%' }}>
        {tagging_list?.map((tag, index) => {
          return renderTaggingList(tag, null, index)
        })}
      </Row>
    </Content>
  )
}

export default IssueTagging
